/**
 * Validation of the login return path.
 *
 * `AuthRoute` preserves the requested path in router state when it bounces an
 * unauthenticated visitor to `/login`. That value is attacker-influenceable —
 * any page can push history state, and a crafted link to `/login` can carry
 * one — so it is a URL the app is about to navigate to, and it must be proved
 * internal before it is trusted.
 *
 * This module is the single place that proof happens. It is a pure function:
 * it reads no browser globals other than the `URL` constructor, it never
 * throws, and it never returns anything but an internal path.
 */

/** Where an untrusted or absent return path falls back to. */
export const DEFAULT_RETURN_PATH = '/events'

/**
 * A fixed, non-resolvable base used only to resolve the candidate path. Any
 * candidate that escapes this origin is off-site by definition.
 */
const SENTINEL_BASE = 'https://admin.invalid'

/**
 * Returning to a login surface after a successful login is a loop and a
 * credential-replay surface, so those paths are refused even though they are
 * internal.
 */
const BLOCKED_PATHNAMES = ['/login', '/reset-password']

/**
 * Resolve an untrusted preserved path into a safe internal navigation target.
 *
 * @param raw the value read from `location.state`, of unknown shape
 * @returns `raw` unchanged when it is a safe internal path (query string and
 *          hash survive), otherwise {@link DEFAULT_RETURN_PATH}
 */
export function safeReturnPath(raw: unknown): string {
  // 1. Anything that is not a non-empty string cannot be a path.
  if (typeof raw !== 'string' || raw.length === 0) return DEFAULT_RETURN_PATH

  // 2. An internal path starts at the root.
  if (raw.charAt(0) !== '/') return DEFAULT_RETURN_PATH

  // 3. `//host` is a protocol-relative URL — it navigates off-origin.
  if (raw.startsWith('//')) return DEFAULT_RETURN_PATH

  // 4. Browsers normalise a backslash to a forward slash, so `/\host` is a
  //    protocol-relative URL in disguise.
  if (raw.includes('\\')) return DEFAULT_RETURN_PATH

  // 5. A leading tab, newline or other control character defeats naive prefix
  //    checks once the browser strips it.
  for (let i = 0; i < raw.length; i += 1) {
    const code = raw.charCodeAt(i)
    if (code < 0x20 || code === 0x7f) return DEFAULT_RETURN_PATH
  }

  // 6. Belt and braces: resolve against the sentinel and insist the origin did
  //    not move. This catches percent-encoded and otherwise exotic vectors the
  //    string guards miss.
  let resolved: URL
  try {
    resolved = new URL(raw, SENTINEL_BASE)
  } catch {
    return DEFAULT_RETURN_PATH
  }
  if (resolved.origin !== SENTINEL_BASE) return DEFAULT_RETURN_PATH

  // 7. Internal, but a login surface.
  if (BLOCKED_PATHNAMES.includes(resolved.pathname.toLowerCase())) {
    return DEFAULT_RETURN_PATH
  }

  // 8. Safe — return the original so any query string and hash survive.
  return raw
}
